/**
 * The build form. Choices cascade: faction narrows the races, a race narrows the classes, and
 * a class narrows the roles, so an illegal character cannot be described in the first place.
 * Picking an imported character replaces that whole cascade with the character's own values.
 * Below the form, the viewer's own and saved builds.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { BUILD_ROLES, type BuildContent, type BuildRole, type BuildScope, type PlayerClass, pageTitle } from '@thottbot/shared';
import { LoginGate } from '../components/LoginGate';
import { ApiError } from '../api/client';
import { useBuildOptions, useCreateBuild } from '../api/builds';
import { CharacterPicker } from '../features/build/CharacterPicker';
import { EquippedPicker, type EquippedItem } from '../features/build/EquippedPicker';
import { YourBuilds } from '../features/build/YourBuilds';

export default function Build() {
  const options = useBuildOptions();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const create = useCreateBuild();

  const [characterId, setCharacterId] = useState<string | null>(params.get('character'));
  const [faction, setFaction] = useState<'Alliance' | 'Horde'>('Alliance');
  const [race, setRace] = useState('');
  const [cls, setCls] = useState<PlayerClass | ''>('');
  const [level, setLevel] = useState(60);
  const [role, setRole] = useState<BuildRole | ''>('');
  const [spec, setSpec] = useState('');
  /** True while `spec` is the picked character's suggestion rather than the player's choice. */
  const [specSuggested, setSpecSuggested] = useState(false);
  const [content, setContent] = useState<BuildContent>('pve');
  const [scope, setScope] = useState<BuildScope>('all');
  const [equipped, setEquipped] = useState<EquippedItem[]>([]);

  const data = options.data;
  const character = data?.characters.find((c) => c.id === characterId) ?? null;

  const races = useMemo(() => (data?.races ?? []).filter((r) => r.faction === faction), [data, faction]);
  const classes = useMemo(() => races.find((r) => r.name === race)?.classes ?? [], [races, race]);
  /**
   * The roles actually on offer. A picked character supplies its own class, so this must not
   * fall back to the hand-entry `cls` (which stays empty) or every role choice would be
   * cleared the moment it was made.
   */
  const roles = useMemo<BuildRole[]>(() => {
    if (!data) return [];
    const chosen = character?.class ?? (cls || null);
    return chosen ? (data.classRoles[chosen as PlayerClass] ?? []) : [];
  }, [cls, character, data]);

  /** The talent trees that fit the chosen class and role. */
  const specs = useMemo<string[]>(() => {
    const chosen = character?.class ?? (cls || null);
    return data && chosen && role ? (data.specs[chosen as PlayerClass]?.[role as BuildRole] ?? []) : [];
  }, [cls, character, data, role]);

  // Clear anything the earlier choice just invalidated, so the form can never submit a
  // combination the server would reject.
  useEffect(() => {
    if (race && !races.some((r) => r.name === race)) setRace('');
  }, [races, race]);
  useEffect(() => {
    if (cls && !classes.includes(cls as PlayerClass)) setCls('');
  }, [classes, cls]);
  useEffect(() => {
    if (role && !roles.includes(role as BuildRole)) setRole('');
  }, [roles, role]);
  // A new role keeps the spec only if it still fits; otherwise the character's suggestion, or the
  // only tree there is, or nothing until the player picks.
  useEffect(() => {
    if (spec && specs.includes(spec)) return;
    const suggested = character?.suggestedSpec;
    if (suggested && specs.includes(suggested)) {
      setSpec(suggested);
      setSpecSuggested(true);
    } else {
      setSpec(specs.length === 1 ? specs[0]! : '');
      setSpecSuggested(false);
    }
  }, [specs, spec, character]);

  if (options.isPending) return <div className="page">Loading...</div>;
  if (!data) return <div className="page">Could not load the build form.</div>;

  const canSubmit = !!role && !!spec && (character ? true : !!race && !!cls) && (data.remaining ?? 0) > 0;

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!role) return;
    create.mutate(
      {
        ...(characterId ? { characterId } : {}),
        class: (character?.class ?? cls) as PlayerClass,
        race: character?.race ?? race,
        faction: (character?.faction ?? faction) as 'Alliance' | 'Horde',
        level: character?.level ?? level,
        role: role as BuildRole,
        spec,
        content,
        scope,
        equipped: character ? [] : equipped.map((i) => ({ artifactId: i.artifactId })),
      },
      {
        onSuccess: (res) => navigate(`/build/${res.build.id}${res.reused ? '?reused=1' : ''}`),
      },
    );
  }

  return (
    <div className="page">
      <title>{pageTitle('Build optimizer')}</title>
      <h1>Build optimizer</h1>

      {!data.configured && <p className="notice error">Build generation is not set up on this server.</p>}
      {data.configured && !data.enabled && <p className="notice">Build generation is switched off for now.</p>}

      {data.configured && data.enabled && (
        <LoginGate action="generate a build">
          <p data-testid="build-allowance">
            {data.remaining ?? 0} of {data.allowance ?? 0} builds left today
          </p>
          {(data.remaining ?? 0) === 0 && <p className="notice">You have used today's builds; more tomorrow.</p>}

          <form className="form" onSubmit={submit}>
            <CharacterPicker characters={data.characters} value={characterId} onChange={setCharacterId} />

            {!character && (
              <>
                <fieldset className="field">
                  <legend>Faction</legend>
                  {(['Alliance', 'Horde'] as const).map((f) => (
                    <label key={f}>
                      <input type="radio" name="faction" value={f} checked={faction === f} onChange={() => setFaction(f)} /> {f}
                    </label>
                  ))}
                </fieldset>

                <div className="field">
                  <label htmlFor="build-race">Race</label>
                  <select id="build-race" value={race} onChange={(e) => setRace(e.target.value)}>
                    <option value="">Choose a race</option>
                    {races.map((r) => (
                      <option key={r.name} value={r.name}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="build-class">Class</label>
                  <select id="build-class" value={cls} onChange={(e) => setCls(e.target.value as PlayerClass)} disabled={!race}>
                    <option value="">Choose a class</option>
                    {classes.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="build-level">Level</label>
                  <input
                    id="build-level"
                    type="number"
                    min={1}
                    max={60}
                    value={level}
                    onChange={(e) => setLevel(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
                  />
                </div>

                <EquippedPicker value={equipped} onChange={setEquipped} />
              </>
            )}

            <fieldset className="field">
              <legend>Role</legend>
              {BUILD_ROLES.filter((r) => roles.includes(r)).map((r) => (
                <label key={r}>
                  <input type="radio" name="role" value={r} checked={role === r} onChange={() => setRole(r)} /> {r}
                </label>
              ))}
              {roles.length === 0 && <p className="muted">Choose a class first.</p>}
            </fieldset>

            <div className="field">
              <label htmlFor="build-spec">Spec</label>
              <select
                id="build-spec"
                value={spec}
                onChange={(e) => {
                  setSpec(e.target.value);
                  setSpecSuggested(false);
                }}
                disabled={specs.length === 0}
              >
                <option value="">{role ? 'Choose a spec' : 'Choose a role first'}</option>
                {specs.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {specSuggested && (
                <span className="muted" data-testid="build-spec-suggested">
                  {' '}
                  (from your talents)
                </span>
              )}
            </div>

            <fieldset className="field">
              <legend>Content</legend>
              {data.contents.map((c) => (
                <label key={c.value}>
                  <input type="radio" name="content" value={c.value} checked={content === c.value} onChange={() => setContent(c.value)} /> {c.label}
                </label>
              ))}
            </fieldset>

            <fieldset className="field">
              <legend>Gear scope</legend>
              {data.scopes.map((s) => (
                <label key={s.value}>
                  <input type="radio" name="scope" value={s.value} checked={scope === s.value} onChange={() => setScope(s.value)} /> {s.label}
                </label>
              ))}
            </fieldset>

            {create.error && <p className="notice error">{describeBuildError(create.error)}</p>}

            <button type="submit" disabled={!canSubmit || create.isPending}>
              {create.isPending ? 'Generating...' : 'Generate build'}
            </button>
          </form>
        </LoginGate>
      )}

      {/* Outside the generation switches, so past builds stay reachable when generation is off. */}
      <YourBuilds />
    </div>
  );
}

/** Build-specific API codes, falling back to the generic wording for everything else. */
export function describeBuildError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Request failed.';
  switch (err.code) {
    case 'allowance_exhausted':
      return "You have used today's builds; more tomorrow.";
    case 'build_in_progress':
      return 'One of your builds is still generating. Wait for it to finish.';
    case 'builds_disabled':
      return 'Build generation is switched off for now.';
    case 'builds_not_configured':
      return 'Build generation is not set up on this server.';
    case 'invalid_character':
      return err.message || 'That character cannot be built.';
    case 'gear_conflict':
      return err.message || 'That item cannot be worn alongside the rest of this gear.';
    case 'invalid_option':
      return 'That item is not one of the options for this slot.';
    case 'not_ready':
      return 'This build is not ready yet.';
    default:
      if (err.status === 429) return 'Slow down: you have hit the build limit for now.';
      return err.message || `Request failed (${err.status})`;
  }
}
